import { v4 as uuidv4 } from 'uuid';
import { AssistantQuery } from '@/components/dashbaord/assistant/types.ts';
import { ExchangeOut } from '@/client';

export const isQueryCompleted = (query: AssistantQuery): boolean => {
	return query.status === 'completed';
};

export const isQueryExecuting = (query: AssistantQuery): boolean => {
	return query.status === 'executing';
};

export const isQueryIdle = (query: AssistantQuery): boolean => {
	return query.status === 'idle';
};

export const isQueryError = (query: AssistantQuery): boolean => {
	return query.status === 'error';
};

export const isQueryCancelled = (query: AssistantQuery): boolean => {
	return query.status === 'cancelled';
};

export const querySelectors = {
	findById: (queries: AssistantQuery[], id: string) => queries.find((q) => q.id === id),
};

export const createCustomQuery = (text: string): AssistantQuery => {
	return {
		id: uuidv4(),
		queryId: null,
		text,
		type: 'user',
		threadId: null,
		response: { text: '', annotations: [] },
		status: 'idle',
		createdAt: new Date(),
	};
};

/**
 * Converts a historical exchange from the API into an AssistantQuery pair.
 * The user message becomes the query text; the assistant message becomes the response.
 * Used when resuming a past session to populate the chat UI with prior turns.
 *
 * `pendingAction` is read straight off this specific exchange, not derived from its position in
 * the session — an open offer can survive a couple of turns, so it may live on an exchange that
 * isn't the newest one. At most one exchange in a session ever has a non-null value; the backend
 * nulls it once an offer is resolved, dropped, or aged out, so there's nothing to re-derive here.
 */
export const createQueryFromExchange = (exchange: ExchangeOut): AssistantQuery => {
	const userMessage = exchange.messages.find((m) => m.role === 'user');
	const assistantMessage = exchange.messages.find((m) => m.role === 'assistant');
	return {
		id: exchange.exchange_id,
		queryId: exchange.exchange_id,
		text: userMessage?.text ?? '',
		type: 'user',
		threadId: null,
		sessionId: exchange.session_id,
		response: assistantMessage ? { text: assistantMessage.text, annotations: [] } : null,
		chunkRefs: exchange.chunk_refs,
		sources: exchange.sources,
		citations: exchange.citations,
		answerFormatVersion: exchange.answer_format_version,
		webSources: exchange.web_sources,
		appliedCorrections: exchange.applied_corrections,
		pendingAction: exchange.pending_action ?? null,
		offeredAction: exchange.offered_action ?? null,
		offerOutcome: exchange.offer_outcome ?? null,
		resolvedOfferExchangeId: exchange.resolved_offer_exchange_id ?? null,
		status: 'completed',
		createdAt: new Date(exchange.created_at),
	};
};

/**
 * Drops the exchange that mechanically answered an earlier offer (a bare "yes" and the resulting
 * "Saved.") from a resumed thread. Its outcome is already folded onto the offering exchange
 * (`offerOutcome`), so rendering this one too would show the same result twice — once as the
 * offer's own outcome line, once as a redundant, confusing "yes" bubble. See
 * docs/local/chat-feature.md §C4.
 */
export const filterResolvedOfferReplies = (queries: AssistantQuery[]): AssistantQuery[] =>
	queries.filter((query) => !query.resolvedOfferExchangeId);

export const createImportantQuery = (systemName: string): AssistantQuery => {
	return {
		id: uuidv4(),
		queryId: null,
		text: `Where is the ${systemName}?`,
		type: 'important',
		threadId: null,
		response: { text: '', annotations: [] },
		status: 'idle',
		createdAt: new Date(),
	};
};
